import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Paper } from "@material-ui/core";
import { Table } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import { useAlert } from "react-alert";
import Pagination from "@material-ui/lab/Pagination";
import { baseUrl } from "../../../services/config";
import {
  cacheImageForOffline,
  extractChallansFromResponse,
  getOfflineChallans,
  getOfflineChallanSyncInfo,
  getOfflinePrintSettings,
  imageUrlToDataUrl,
  replaceOfflineChallans,
  saveOfflineInstructions,
  saveOfflinePrintSettings,
} from "../../../services/offlineChallanDb";
import MultiStudentFeeChallan from "../MultiStudentFeeChallan/MultiStudentFeeChallan";
import "../StudentWisePrint/myprint.css";
import "../../../components/PrintChallanModal/printChallan.style.css";

const AUTO_SYNC_INTERVAL_MS = 60 * 60 * 1000;
const AUTO_SYNC_CHECK_INTERVAL_MS = 60 * 1000;
const AUTO_SYNC_RETRY_BASE_MS = 5 * 60 * 1000;
const API_TIMEOUT_MS = 60000;

const isSyncInfoStale = (syncInfo) => {
  if (!syncInfo || !syncInfo.syncedAt) return true;

  const syncedAt = new Date(syncInfo.syncedAt).getTime();
  return (
    !Number.isFinite(syncedAt) || Date.now() - syncedAt >= AUTO_SYNC_INTERVAL_MS
  );
};

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLocaleUpperCase();

const getStudentDetails = (student) =>
  Array.isArray(student.fee_challans_detials)
    ? student.fee_challans_detials
    : [];

const getFeeAmount = (student) =>
  getStudentDetails(student).reduce(
    (total, detail) => total + (Number(detail.amount) || 0),
    0,
  );

const getChallanCount = (student) =>
  new Set(
    getStudentDetails(student).map(
      (detail) =>
        detail.fee_challan_id ||
        (detail.fee_challan && detail.fee_challan.id) ||
        detail.id,
    ),
  ).size;

const getUniqueOptions = (students, idKey, relationKey, flatNameKey) => {
  const options = new Map();

  students.forEach((student) => {
    const relation = student[relationKey];
    const id = student[idKey] || (relation && relation.id);
    const name = student[flatNameKey] || (relation && relation.name);

    if (id !== undefined && id !== null && name) {
      options.set(String(id), String(name));
    }
  });

  return Array.from(options, ([id, name]) => ({ id, name })).sort((a, b) =>
    a.id.localeCompare(b.id, undefined, { numeric: true }),
  );
};

const OfflineStudentWisePrint = () => {
  const alert = useAlert();
  const printRef = useRef(null);
  const synchronizeRef = useRef(null);
  const syncInfoRef = useRef(null);
  const isSyncingRef = useRef(false);
  const autoSyncFailureCountRef = useRef(0);
  const nextAutoSyncAttemptAtRef = useRef(0);
  const printResourcesNeedSyncRef = useRef(true);
  const authtoken = localStorage.getItem("userToken");
  const [students, setStudents] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [campusId, setCampusId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [syncInfo, setSyncInfo] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [blankPage, setBlankPage] = useState(false);
  const [printResourceVersion, setPrintResourceVersion] = useState(0);

  const loadCachedStudents = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        cachedStudents,
        cachedSyncInfo,
        cachedPrintSettings,
      ] = await Promise.all([
        getOfflineChallans(),
        getOfflineChallanSyncInfo(),
        getOfflinePrintSettings(),
      ]);
      setStudents(cachedStudents);
      setSyncInfo(cachedSyncInfo);
      syncInfoRef.current = cachedSyncInfo;
      printResourcesNeedSyncRef.current =
        !cachedPrintSettings ||
        cachedPrintSettings._offlineLogoResolved !== true;
      setSelectedIds([]);
      return cachedSyncInfo;
    } catch (error) {
      console.log(error);
      alert.error(error.message || "Unable to load offline challans.");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [alert]);

  useEffect(() => {
    const requestAutomaticSync = () => {
      if (
        !navigator.onLine ||
        document.visibilityState === "hidden" ||
        Date.now() < nextAutoSyncAttemptAtRef.current ||
        (!isSyncInfoStale(syncInfoRef.current) &&
          !printResourcesNeedSyncRef.current)
      ) {
        return;
      }

      if (synchronizeRef.current) {
        synchronizeRef.current({ silent: true });
      }
    };

    const handleOnline = () => {
      setIsOnline(true);
      autoSyncFailureCountRef.current = 0;
      nextAutoSyncAttemptAtRef.current = 0;
      requestAutomaticSync();
    };
    const handleOffline = () => setIsOnline(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") requestAutomaticSync();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("focus", requestAutomaticSync);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    loadCachedStudents().then(requestAutomaticSync);
    const intervalId = window.setInterval(
      requestAutomaticSync,
      AUTO_SYNC_CHECK_INTERVAL_MS,
    );

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("focus", requestAutomaticSync);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [loadCachedStudents]);

  const campuses = useMemo(
    () => getUniqueOptions(students, "campus_id", "campus", "campus_name"),
    [students],
  );
  const classes = useMemo(
    () =>
      getUniqueOptions(
        students,
        "student_class_id",
        "student_class",
        "student_class_name",
      ),
    [students],
  );
  const sections = useMemo(
    () =>
      getUniqueOptions(
        students,
        "global_section_id",
        "global_section",
        "global_section_name",
      ),
    [students],
  );

  const filteredStudents = useMemo(() => {
    const search = normalize(searchText);

    return students.filter((student) => {
      const details = getStudentDetails(student);
      const searchableValues = [
        student.admission_id,
        student.registration_id,
        student.name,
        student.father_name,
        ...details.map(
          (detail) =>
            detail.challan_no ||
            (detail.fee_challan && detail.fee_challan.challan_no),
        ),
      ];

      const matchesSearch =
        !search ||
        searchableValues.some((value) => normalize(value).includes(search));
      const matchesCampus =
        !campusId ||
        String(student.campus_id || (student.campus && student.campus.id)) ===
          campusId;
      const matchesClass =
        !classId ||
        String(
          student.student_class_id ||
            (student.student_class && student.student_class.id),
        ) === classId;
      const matchesSection =
        !sectionId ||
        String(
          student.global_section_id ||
            (student.global_section && student.global_section.id),
        ) === sectionId;

      return matchesSearch && matchesCampus && matchesClass && matchesSection;
    });
  }, [students, searchText, campusId, classId, sectionId]);

  const [page, setPage] = useState(1);
  const PER_PAGE = 20;
  const count = Math.ceil(filteredStudents.length / PER_PAGE);

  const handleChange = (e, p) => {
    setPage(p);
  };

  useEffect(() => {
    setPage(1);
  }, [searchText, campusId, classId, sectionId, students]);

  const pageStudents = useMemo(() => {
    const begin = (page - 1) * PER_PAGE;
    return filteredStudents.slice(begin, begin + PER_PAGE);
  }, [filteredStudents, page]);

  const selectedStudents = useMemo(
    () =>
      students.filter((student) => selectedIds.includes(String(student.id))),
    [students, selectedIds],
  );

  const allVisibleSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((student) =>
      selectedIds.includes(String(student.id)),
    );

  const fetchApiData = async (path) => {
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    const timeoutId = controller
      ? setTimeout(() => {
          controller.abort();
        }, API_TIMEOUT_MS)
      : null;

    try {
      const response = await fetch(`${baseUrl}${path}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${authtoken}`,
        },
        signal: controller?.signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status}) for ${path}.`);
      }

      const data = await response.json();

      if (data.metadata?.success === false) {
        throw new Error(data.metadata.message || `Request failed for ${path}.`);
      }

      return data;
    } catch (error) {
      if (error?.name === "AbortError") {
        throw new Error(
          `Request timed out for ${path} after ${API_TIMEOUT_MS}ms.`,
        );
      }

      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };

  const syncPrintResources = async () => {
    const warnings = [];

    try {
      const settingsResponse = await fetchApiData("/get-settings");
      const settings = settingsResponse.payload.settings;
      let offlineLogo = settings.logo_file;
      let offlineLogoResolved = true;

      try {
        offlineLogo = await imageUrlToDataUrl(settings.logo_file);
      } catch (error) {
        console.log(error);
        try {
          offlineLogo = await cacheImageForOffline(settings.logo_file);
        } catch (cacheError) {
          console.log(cacheError);
          offlineLogo = "";
          offlineLogoResolved = false;
          warnings.push("the logo could not be stored locally");
        }
      }

      await saveOfflinePrintSettings({
        ...settings,
        logo_file: offlineLogo,
        _offlineLogoResolved: offlineLogoResolved,
      });
    } catch (error) {
      console.log(error);
      warnings.push("print settings were not updated");
    }

    try {
      const instructionsResponse = await fetchApiData("/challan-instructions");
      const instructions = Array.isArray(instructionsResponse.payload)
        ? instructionsResponse.payload
        : [];
      const offlineInstructions = await Promise.all(
        instructions.map(async (instruction) => {
          if (!instruction.logo_file_url) return instruction;

          try {
            return {
              ...instruction,
              logo_file_url: await imageUrlToDataUrl(instruction.logo_file_url),
            };
          } catch (error) {
            console.log(error);
            try {
              return {
                ...instruction,
                logo_file_url: await cacheImageForOffline(
                  instruction.logo_file_url,
                ),
              };
            } catch (cacheError) {
              console.log(cacheError);
              warnings.push(
                `instruction logo ${instruction.id ||
                  ""} could not be stored locally`,
              );
              return { ...instruction, logo_file_url: "" };
            }
          }
        }),
      );
      await saveOfflineInstructions(offlineInstructions);
    } catch (error) {
      console.log(error);
      warnings.push("challan instructions were not updated");
    }

    return warnings;
  };

  const synchronize = async ({ silent = false } = {}) => {
    if (!navigator.onLine) {
      if (!silent) {
        alert.error("Connect to the internet before synchronizing challans.");
      }
      return false;
    }

    if (isSyncingRef.current) return false;

    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      const responseData = await fetchApiData("/all-fee-challans");
      const syncedStudents = extractChallansFromResponse(responseData);

      if (
        !responseData.payload ||
        !Array.isArray(responseData.payload.students)
      ) {
        throw new Error(
          "The challan response does not contain payload.students.",
        );
      }

      const newSyncInfo = await replaceOfflineChallans(syncedStudents);
      const warnings = await syncPrintResources();

      setStudents(syncedStudents);
      setSyncInfo(newSyncInfo);
      syncInfoRef.current = newSyncInfo;
      setSelectedIds([]);
      setPrintResourceVersion((current) => current + 1);
      autoSyncFailureCountRef.current = 0;
      printResourcesNeedSyncRef.current = warnings.length > 0;
      nextAutoSyncAttemptAtRef.current = warnings.length
        ? Date.now() + AUTO_SYNC_RETRY_BASE_MS
        : 0;

      const warningText = warnings.length
        ? ` Note: ${warnings.join("; ")}.`
        : "";
      if (!silent) {
        alert.success(
          `${syncedStudents.length} students are ready for offline printing.${warningText}`,
        );
      }
      return true;
    } catch (error) {
      console.log(error);
      if (silent) {
        autoSyncFailureCountRef.current += 1;
        const retryDelay = Math.min(
          AUTO_SYNC_RETRY_BASE_MS * 2 ** (autoSyncFailureCountRef.current - 1),
          AUTO_SYNC_INTERVAL_MS,
        );
        nextAutoSyncAttemptAtRef.current = Date.now() + retryDelay;
      } else {
        alert.error(error.message || "Unable to synchronize offline challans.");
      }
      return false;
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  };

  synchronizeRef.current = synchronize;

  const toggleStudent = (studentId) => {
    const id = String(studentId);
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  };

  const toggleAllVisible = () => {
    const visibleIds = filteredStudents.map((student) => String(student.id));

    if (allVisibleSelected) {
      setSelectedIds((current) =>
        current.filter((selectedId) => !visibleIds.includes(selectedId)),
      );
      return;
    }

    setSelectedIds((current) =>
      Array.from(new Set([...current, ...visibleIds])),
    );
  };

  const pageStyle = `
    @page {
      size: letter landscape !important;
    }
  `;

  return (
    <Paper style={{ marginTop: 10, marginBottom: 10, padding: 15 }}>
      <div className={`alert ${isOnline ? "alert-info" : "alert-warning"}`}>
        <strong>{isOnline ? "Online" : "Offline"}</strong>
        {syncInfo ? (
          <span>
            {` - local snapshot: ${
              syncInfo.count
            } students, synchronized ${new Date(
              syncInfo.syncedAt,
            ).toLocaleString()}.`}
          </span>
        ) : (
          <span> - no offline challan snapshot has been synchronized.</span>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="col-md-8">
          <label className="form-control-label">
            <b>Admission ID, Name & Father Name</b>
          </label>
          <input
            className="form-control"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search Admission ID, Name & Father Name"
          />
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!isOnline || isSyncing}
          onClick={synchronize}
        >
          {isSyncing ? "Synchronizing..." : "Synchronize Now"}
        </button>
      </div>

      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <label className="form-control-label">
            <b>Select Campus</b>
          </label>
          <select
            className="form-control"
            value={campusId}
            onChange={(event) => setCampusId(event.target.value)}
          >
            <option value="">All campuses</option>
            {campuses.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-control-label">
            <b>Select Class</b>
          </label>
          <select
            className="form-control"
            value={classId}
            onChange={(event) => setClassId(event.target.value)}
          >
            <option value="">All classes</option>
            {classes.map((studentClass) => (
              <option key={studentClass.id} value={studentClass.id}>
                {studentClass.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-control-label">
            <b>Select Section</b>
          </label>
          <select
            className="form-control"
            value={sectionId}
            onChange={(event) => setSectionId(event.target.value)}
          >
            <option value="">All sections</option>
            {sections.map((section) => (
              <option key={section.id} value={section.id}>
                {section.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="d-flex align-items-center mb-3">
        <ReactToPrint
          pageStyle={pageStyle}
          documentTitle="Offline Challan Copy"
          removeAfterPrint={true}
          trigger={() => (
            <button
              type="button"
              className="btn btn-primary mr-4"
              disabled={selectedStudents.length === 0}
            >
              Print Selected ({selectedStudents.length})
            </button>
          )}
          content={() => printRef.current}
        />
        <label className="checkbox checkbox-single mb-0">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={blankPage}
            onChange={(event) => setBlankPage(event.target.checked)}
          />
          <span />
          &nbsp;&nbsp;Use blank page for print
        </label>
      </div>

      {isLoading ? (
        <span className="spinner-border"></span>
      ) : (
        <Table responsive hover>
          <thead>
            <tr className="text-center">
              <th style={{ width: 45 }}>
                <label className="checkbox checkbox-single">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label="Select all visible students"
                  />
                  <span />
                </label>
              </th>
              <th>Admission ID</th>
              <th>Student</th>
              <th>Father Name</th>
              <th>Class / Section</th>
              <th>Campus</th>
              <th>Challans</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {pageStudents.map((student) => (
              <tr key={student.id} className="text-center">
                <td>
                  <label className="checkbox checkbox-single">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(String(student.id))}
                      onChange={() => toggleStudent(student.id)}
                      aria-label={`Select ${student.name}`}
                    />
                    <span />
                  </label>
                </td>
                <td>{student.admission_id || student.registration_id}</td>
                <td>{student.name}</td>
                <td>{student.father_name}</td>
                <td>
                  {student.student_class_name ||
                    (student.student_class && student.student_class.name)}
                  {" / "}
                  {student.global_section_name ||
                    (student.global_section && student.global_section.name)}
                </td>
                <td>
                  {student.campus_name ||
                    (student.campus && student.campus.name)}
                </td>
                <td>{getChallanCount(student)}</td>
                <td>{getFeeAmount(student).toLocaleString()}</td>
              </tr>
            ))}
            {filteredStudents.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center p-5 text-muted">
                  {students.length === 0
                    ? "Synchronize challans while online to create the offline snapshot."
                    : "No stored challans match the current filters."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Pagination
        color="text-primary"
        count={count}
        size="large"
        page={page}
        variant="text"
        shape="round"
        onChange={handleChange}
      />

      <div ref={printRef} id="printSection" className="challan-print-source">
        <MultiStudentFeeChallan
          key={printResourceVersion}
          allstudentList={selectedStudents}
          blankPage={blankPage}
          offlineOnly={true}
        />
      </div>
    </Paper>
  );
};

export default OfflineStudentWisePrint;
